/**
 * Prepara a imagem antes de mandar para o OCR.
 *
 * Este arquivo nasceu de um sintoma concreto: o leitor de etiqueta so acertava
 * o codigo de rastreio: nome, bloco e apartamento nunca vinham. Nao era o
 * parser — era o que chegava nele. O frame ia CRU para o Tesseract: colorido,
 * na escala original, com o balcao e a mao do porteiro no enquadramento. O
 * rastreio sobrevive a isso porque e fonte grande e alto contraste; "Apto 302"
 * em corpo 8, fotografado de esguelha sob luz de portaria, nao sobrevive.
 *
 * Tres etapas, nesta ordem, e cada uma resolve uma coisa:
 *
 * 1. CINZA — o OCR nao usa cor, e a conversao por luminancia preserva o
 *    contraste que a media dos canais destroi (texto azul sobre fundo branco
 *    vira cinza-claro sobre branco na media simples).
 * 2. CONTRASTE por percentil — etiqueta fotografada raramente usa a faixa
 *    inteira de 0 a 255; costuma viver entre 90 e 190. Esticar essa faixa
 *    separa letra de papel. Por percentil, e nao por minimo e maximo, porque um
 *    unico pixel estourado pelo flash anularia o ajuste inteiro.
 * 3. REESCALA — o LSTM do Tesseract quer altura de letra por volta de 30px.
 *    Numa etiqueta a 1080p o endereco tem 15 a 20px, entao o normal e AMPLIAR
 *    (depois de normalizar o contraste, e nao antes, para nao interpolar
 *    ruido). Acima do teto acontece o contrario: o quadro e REDUZIDO, porque o
 *    custo do LSTM cresce com a area e camera que ignora o `ideal: 1920`
 *    entrega recorte grande demais para ler em tempo de balcao.
 *
 * NAO binarizamos. O Tesseract ja aplica Otsu por conta propria, e sobre uma
 * imagem em cinza bem-comportada ele decide melhor do que nos: binarizar aqui
 * com luz irregular — sombra da mao sobre metade da etiqueta — apagaria de vez
 * o texto do lado escuro, sem chance de recuperacao depois.
 */

/** Onde o LSTM comeca a ler bem. Acima disso so gastamos memoria e tempo. */
const TARGET_WIDTH = 2000;
/**
 * Teto de verdade: acima disto o quadro e REDUZIDO antes de ir ao OCR.
 *
 * O custo do LSTM cresce com a area, entao um recorte de camera 4K (comum
 * quando o aparelho ignora o `ideal: 1920` que pedimos) chega com mais de tres
 * vezes a area de um de 1080p — e vira a diferenca entre ler em segundos e o
 * porteiro achar que travou.
 */
const MAX_WIDTH = 2600;
/** Recorte da cauda do histograma; 2% de cada lado absorve flash e sombra. */
const CLIP_RATIO = 0.02;

/**
 * Escala de cinza + contraste esticado + reescala para a faixa que o OCR le.
 *
 * Devolve um canvas novo — o original continua servindo para o codigo de
 * barras, que le melhor na imagem sem tratamento.
 */
export function preprocessForOcr(source: HTMLCanvasElement): HTMLCanvasElement {
  const context = source.getContext('2d', { willReadFrequently: true });
  if (!context || source.width === 0 || source.height === 0) return source;

  const image = context.getImageData(0, 0, source.width, source.height);
  const pixels = image.data;

  // --- 1. cinza, guardando o histograma na mesma passada ---------------------
  const histogram = new Uint32Array(256);
  const gray = new Uint8ClampedArray(source.width * source.height);

  for (let index = 0, at = 0; index < pixels.length; index += 4, at += 1) {
    const value =
      (pixels[index] ?? 0) * 0.299 +
      (pixels[index + 1] ?? 0) * 0.587 +
      (pixels[index + 2] ?? 0) * 0.114;
    const rounded = value < 0 ? 0 : value > 255 ? 255 : Math.round(value);
    gray[at] = rounded;
    histogram[rounded] = (histogram[rounded] ?? 0) + 1;
  }

  // --- 2. contraste: acha os percentis e estica o que sobra ------------------
  const total = gray.length;
  const clip = Math.floor(total * CLIP_RATIO);

  let low = 0;
  for (let seen = 0, value = 0; value < 256; value += 1) {
    seen += histogram[value] ?? 0;
    if (seen > clip) {
      low = value;
      break;
    }
  }

  let high = 255;
  for (let seen = 0, value = 255; value >= 0; value -= 1) {
    seen += histogram[value] ?? 0;
    if (seen > clip) {
      high = value;
      break;
    }
  }

  // Imagem chapada (etiqueta estourada, foto do teto): esticar dividiria por
  // quase zero e transformaria ruido em textura. Melhor deixar como esta e
  // deixar o OCR falhar honestamente.
  const span = high - low;
  const stretch = span >= 16;

  for (let index = 0, at = 0; index < pixels.length; index += 4, at += 1) {
    const value = gray[at] ?? 0;
    const adjusted = stretch ? ((value - low) * 255) / span : value;
    const clamped = adjusted < 0 ? 0 : adjusted > 255 ? 255 : adjusted;
    pixels[index] = clamped;
    pixels[index + 1] = clamped;
    pixels[index + 2] = clamped;
    pixels[index + 3] = 255;
  }

  // --- 3. reescala -----------------------------------------------------------
  //
  // O `MAX_WIDTH` era um teto que nao limitava nada: a escala saia menor que 1
  // quando o recorte vinha maior que ele, e o `scale <= 1` logo abaixo
  // devolvia a imagem no tamanho original. Ou seja, exatamente no caso que o
  // teto existia para cobrir — camera entregando mais que o pedido, recorte
  // grande — o Tesseract recebia o quadro inteiro e demorava vezes mais.
  //
  // Agora a escala anda para os dois lados: amplia o que e pequeno demais para
  // o LSTM e REDUZ o que passa do teto. So o tamanho ja adequado sai sem
  // reamostragem.
  const scale = Math.min(
    Math.max(1, TARGET_WIDTH / source.width),
    MAX_WIDTH / source.width,
  );

  const flat = document.createElement('canvas');
  flat.width = source.width;
  flat.height = source.height;
  flat.getContext('2d')?.putImageData(image, 0, 0);

  if (scale === 1) return flat;

  const output = document.createElement('canvas');
  output.width = Math.round(source.width * scale);
  output.height = Math.round(source.height * scale);

  const target = output.getContext('2d');
  if (!target) return flat;
  target.imageSmoothingEnabled = true;
  target.imageSmoothingQuality = 'high';
  target.drawImage(flat, 0, 0, output.width, output.height);

  return output;
}
